import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ChevronRight,
  ChevronLeft,
  User,
  Car,
  FileText,
  DollarSign,
  CreditCard,
  Check,
  Loader2,
  AlertCircle,
  Calendar,
  X,
  Sparkles,
  MessageSquare,
} from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';
import CustomerPhotoUpload from './CustomerPhotoUpload';
import { uploadCustomerPhoto } from '../supabase/storage';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../supabase';
import { trackEvent, getTrackingPayload } from '../utils/metaTracking';
import {
  carMakes,
  carModels,
  years,
  ONE_TIME_PACKAGES,
  PROMO_EXPIRY_ISO,
  SEAT_PROMO_CODE,
  SEAT_PROMO_RATE,
  LAUNCH_PROMO_CODE,
  LAUNCH_PROMO_RATE,
  LAUNCH_PROMO_CAP,
  FALLBACK_PROMO_CODE,
  ONE_TIME_WEEKDAY_SLOTS,
  ONE_TIME_SATURDAY_SLOTS,
  getOneTimeDates,
  formatOnetimeDateId,
  isClosedBookingDate,
  markBookedDays,
  RUSH_FEE,
  SHOP_PHONE_SMS,
  getOnetimeSlotId,
  formatOnetimeSlotLabel,
  INTERIOR_VEHICLE_TYPES,
  detectVehicleType,
  ADD_ONS,
} from '../data/constants';

// ==========================================
// VALIDATION
// ==========================================
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const sanitizeInput = (str) => {
  if (typeof str !== 'string') return str;
  return str.replace(/[<>]/g, '').replace(/javascript:/gi, '').trim();
};

const sanitizeFormData = (data) => {
  if (data === null || data === undefined) return data;
  if (typeof data === 'string') return sanitizeInput(data);
  if (typeof data === 'boolean' || typeof data === 'number') return data;
  if (Array.isArray(data)) return data.map(sanitizeFormData);
  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    sanitized[key] = sanitizeFormData(value);
  }
  return sanitized;
};

const validateStep = (step, formData) => {
  const errors = [];
  if (step === 1) {
    const { name, phone, email, homeAddress, county } = formData.personalInfo;
    const { make, model, year, color } = formData.vehicleInfo;
    if (!name.trim()) errors.push('Full name is required');
    if (!phone.trim()) errors.push('Phone number is required');
    else if (phone.length !== 10) errors.push('Phone must be exactly 10 digits');
    if (!formData.smsConsent) errors.push('Please agree to receive SMS notifications');
    if (!email.trim()) errors.push('Email address is required');
    else if (!EMAIL_REGEX.test(email.trim())) errors.push('Please enter a valid email address');
    if (!homeAddress.trim()) errors.push('Service address is required');
    if (!county) errors.push('Please select your county');
    const { makeOther, modelOther } = formData.vehicleInfo;
    if (!make) errors.push('Vehicle make is required');
    else if (make === 'other' && !makeOther.trim()) errors.push('Please type your vehicle make');
    if (!model) errors.push('Vehicle model is required');
    else if (model === 'other' && !modelOther.trim()) errors.push('Please type your vehicle model');
    if (!year) errors.push('Vehicle year is required');
    if (!color.trim()) errors.push('Vehicle color is required');
  }
  if (step === 2) {
    if (!formData.selectedSlot) errors.push('Please select a date and time');
  }
  if (step === 3) {
    if (!formData.acceptedAgreement) errors.push('You must accept the Detail Service Agreement');
    if (!formData.signature.trim()) errors.push('Digital signature is required');
  }
  return errors;
};

// ADD_ONS imported from constants (identical across all booking types)

// ==========================================
// ONE-TIME CALENDAR
// ==========================================
const OneTimeCalendar = ({ selectedSlot, onSelectSlot }) => {
  const [bookedSlots, setBookedSlots] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [activeDate, setActiveDate] = useState(null);
  const stripRef = useRef(null);

  const dates = getOneTimeDates(0, 60);

  useEffect(() => {
    const loadBooked = async () => {
      try {
        const [{ data: otData, error: otError }, { data: mrData }] = await Promise.all([
          supabase.from('one_time_slots').select('id, is_booked').eq('is_booked', true),
          supabase.from('maintenance_reset_slots').select('id').eq('is_booked', true),
        ]);
        if (otError) throw otError;
        // One car per day: any booking (either calendar) fills that whole day.
        setBookedSlots(markBookedDays([...(otData || []), ...(mrData || [])].map((r) => r.id)));
      } catch {
        // Graceful fallback — show all slots as available
      } finally {
        setIsLoading(false);
      }
    };
    loadBooked();
  }, []);

  const scrollStrip = (dir) => {
    if (!stripRef.current) return;
    stripRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  const handleDateSelect = (date) => {
    const dateId = formatOnetimeDateId(date);
    if (activeDate && formatOnetimeDateId(activeDate) === dateId) {
      setActiveDate(null);
    } else {
      setActiveDate(date);
      if (selectedSlot && !selectedSlot.startsWith(dateId)) {
        onSelectSlot(null);
      }
    }
  };

  const handleTimeSelect = (date, timeSlotId) => {
    const slotId = getOnetimeSlotId(date, timeSlotId);
    if (isClosedBookingDate(date) || bookedSlots[slotId]) return;
    onSelectSlot(slotId === selectedSlot ? null : slotId);
  };

  const isSaturday = (date) => date.getDay() === 6;

  const getSlotsForDate = (date) =>
    isSaturday(date) ? ONE_TIME_SATURDAY_SLOTS : ONE_TIME_WEEKDAY_SLOTS;

  const isDateFullyBooked = (date) =>
    isClosedBookingDate(date) ||
    getSlotsForDate(date).every((ts) => bookedSlots[getOnetimeSlotId(date, ts.id)]);

  const DAY_ABBRS = ['', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const activeDateId = activeDate ? formatOnetimeDateId(activeDate) : null;

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border-l-8 border-[#68caf1]">
      <div className="flex items-center gap-3 mb-2">
        <Calendar className="w-8 h-8 text-[#68caf1]" />
        <h3 className="text-2xl font-bold text-[#333333]">Choose Your Date</h3>
      </div>
      <p className="text-gray-500 text-sm mb-1 ml-11">
        Mon–Sat · 9:00 AM
      </p>
      {isLoading && (
        <p className="text-xs text-gray-400 ml-11 mb-4">Loading availability...</p>
      )}
      {!isLoading && (
        <p className="text-xs text-gray-400 ml-11 mb-4">All slots shown — any unavailable are grayed out</p>
      )}

      {/* Date Strip */}
      <div className="relative">
        <button
          onClick={() => scrollStrip(-1)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md border border-gray-200 rounded-full w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-all -ml-3"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div
          ref={stripRef}
          className="flex gap-3 overflow-x-auto scroll-smooth pb-2 px-6"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {dates.map((date) => {
            const dateId = formatOnetimeDateId(date);
            const isSat = isSaturday(date);
            const isClosed = isClosedBookingDate(date);
            const isFullyBooked = isDateFullyBooked(date);
            const isActive = activeDateId === dateId;
            const hasSelectedSlot = selectedSlot && selectedSlot.startsWith(dateId);
            return (
              <button
                key={dateId}
                onClick={() => !isFullyBooked && handleDateSelect(date)}
                disabled={isFullyBooked}
                className={`flex-shrink-0 flex flex-col items-center px-4 py-3 rounded-2xl border-2 transition-all duration-200 min-w-[72px] ${
                  isFullyBooked
                    ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                    : isActive || hasSelectedSlot
                    ? 'border-[#68caf1] bg-[#68caf1] text-black shadow-lg scale-105'
                    : 'border-gray-200 bg-white text-[#333333] hover:border-[#68caf1] hover:shadow-md'
                }`}
              >
                <span className={`text-xs font-bold uppercase tracking-wider mb-1 ${
                  isActive || hasSelectedSlot ? 'text-black/70' : isSat ? 'text-orange-500' : 'text-sky-600'
                }`}>
                  {DAY_ABBRS[date.getDay()]}
                </span>
                <span className="text-lg font-bold leading-none">{date.getDate()}</span>
                <span className={`text-xs mt-1 ${isActive || hasSelectedSlot ? 'text-black/60' : 'text-gray-500'}`}>
                  {date.toLocaleDateString('en-US', { month: 'short' })}
                </span>
                {isFullyBooked && <span className="text-xs text-gray-400 mt-0.5">{isClosed ? 'Closed' : 'Full'}</span>}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => scrollStrip(1)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md border border-gray-200 rounded-full w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-all -mr-3"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Time Slot Panel */}
      {activeDate && (
        <div className="mt-6">
          <div className="bg-gray-50 rounded-xl p-5 border-2 border-gray-100">
            <p className="text-sm font-bold text-[#333333] mb-4">
              {activeDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              <span className="text-gray-400 font-normal"> — pick a time</span>
            </p>
            <div className="grid gap-3 grid-cols-2">
              {getSlotsForDate(activeDate).map((timeSlot) => {
                const slotId = getOnetimeSlotId(activeDate, timeSlot.id);
                const isBooked = !!bookedSlots[slotId] || isClosedBookingDate(activeDate);
                const isSelected = selectedSlot === slotId;
                return (
                  <button
                    key={slotId}
                    onClick={() => handleTimeSelect(activeDate, timeSlot.id)}
                    disabled={isBooked}
                    className={`py-4 rounded-xl font-bold text-center transition-all duration-200 ${
                      isSelected
                        ? 'bg-[#68caf1] text-black shadow-lg scale-[1.02]'
                        : isBooked
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white border-2 border-[#68caf1] text-[#333333] hover:bg-[#68caf1]/10 hover:scale-[1.01] shadow-sm'
                    }`}
                  >
                    <div className="text-sm">{timeSlot.label}</div>
                    <div className={`text-xs mt-1 font-normal ${
                      isSelected ? 'text-black/70' : isBooked ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {isBooked ? 'Unavailable' : timeSlot.time}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!activeDate && !selectedSlot && (
        <p className="text-center text-gray-400 text-sm mt-6">
          ← Swipe or tap a date above to see available times
        </p>
      )}

      {selectedSlot && (
        <div className="mt-5 bg-[#68caf1]/10 border-2 border-[#68caf1] rounded-xl p-4 flex items-center justify-between">
          <p className="text-sm font-bold text-[#333333]">
            ✓ {formatOnetimeSlotLabel(selectedSlot)}
          </p>
          <button
            onClick={() => { onSelectSlot(null); setActiveDate(null); }}
            className="text-xs text-gray-500 hover:text-red-500 font-semibold transition-colors"
          >
            Change
          </button>
        </div>
      )}
    </div>
  );
};

// ==========================================
// AGREEMENT MODAL
// ==========================================
const OneTimeAgreementModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#68caf1] px-8 py-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-black">One-Time Detail Service Agreement</h2>
          <button onClick={onClose} className="text-black hover:text-gray-700 transition-all">
            <X className="w-8 h-8" />
          </button>
        </div>
        <div className="p-8 space-y-6 text-gray-700 text-sm">
          <p className="font-semibold text-base">Terms and Conditions</p>
          <div className="space-y-4">
            <div>
              <p className="font-semibold">1. One-Time Service</p>
              <p>This is a single-session detail booking. It is not a recurring subscription or long-term commitment. You may book additional services at any time.</p>
            </div>
            <div>
              <p className="font-semibold">2. Service Location & Vehicle Access</p>
              <p>Service is performed at the address provided. You agree to have the vehicle accessible at the scheduled time. Remove all personal belongings prior to service. Monarch Detailing is not responsible for lost, damaged, or stolen personal items left in the vehicle.</p>
            </div>
            <div>
              <p className="font-semibold">3. Payment</p>
              <p>A 50% deposit is collected at booking to reserve your slot. The remaining balance is due at the time of service. Add-on charges are communicated and must be mutually agreed upon before work begins.</p>
            </div>
            <div>
              <p className="font-semibold">4. Inspection-Only Charges</p>
              <p>Certain conditions can only be identified upon physical inspection and may result in additional charges. These include biohazard cleaning, heavy stain extraction beyond scope, excessive pet hair, and severe soiling. All charges are quoted on-site before work begins — you must approve before any additional work starts.</p>
            </div>
            <div>
              <p className="font-semibold">5. Pre-Existing Damage</p>
              <p>Monarch Detailing is not liable for pre-existing scratches, swirl marks, paint chips, rust, trim damage, or any condition present before service begins.</p>
            </div>
            <div>
              <p className="font-semibold">6. Limitation of Liability</p>
              <p>Monarch Detailing's maximum liability shall not exceed the total amount paid for that specific service.</p>
            </div>
            <div>
              <p className="font-semibold">7. Right to Refuse Service</p>
              <p>Monarch Detailing reserves the right to refuse or discontinue service if the vehicle condition presents a health or safety risk.</p>
            </div>
            <div>
              <p className="font-semibold">8. Rescheduling & Cancellations</p>
              <p>Rescheduling requires at least 48 hours notice. Cancellations with less than 48 hours notice may forfeit the booking deposit.</p>
            </div>
            <div>
              <p className="font-semibold">9. Force Majeure / Weather</p>
              <p>Services may be rescheduled due to severe weather at no penalty to either party.</p>
            </div>
            <div>
              <p className="font-semibold">10. Quality Guarantee</p>
              <p>If you are not satisfied with any aspect of the service, notify us within 24 hours and we will make it right.</p>
            </div>
            <div>
              <p className="font-semibold">11. SMS & Text Message Consent</p>
              <p>By providing your phone number, you consent to receive SMS text messages from Monarch Detailing at the number provided. Messages may include appointment confirmations, en-route notifications, invoice delivery, and service-related updates. Message and data rates may apply. Reply STOP to any message to unsubscribe at any time.</p>
            </div>
            <div>
              <p className="font-semibold">12. Vehicle Photography</p>
              <p>Monarch Detailing may photograph your vehicle before, during, and after service for quality assurance, documentation, and portfolio purposes. Photos may be used on social media and marketing materials. Vehicle identifying information (license plates) will be removed or obscured before any public use.</p>
            </div>
            <div>
              <p className="font-semibold">13. Electronic Signature</p>
              <p>By submitting this form and signing below, you agree to all terms and conditions and confirm you are the registered owner or an authorized agent for the vehicle being serviced.</p>
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-gray-50 px-8 py-6 border-t-2 border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#68caf1] text-black px-10 py-3 rounded-lg font-bold hover:bg-[#5bb8db] transition-all"
          >
            I Understand — Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// STEP DEFINITIONS
// ==========================================
const STEPS = [
  { id: 1, label: 'Your Details', icon: User },
  { id: 2, label: 'Pick a Date', icon: Calendar },
  { id: 3, label: 'Review & Pay', icon: DollarSign },
];

// ==========================================
// ONE-TIME BOOKING WIZARD
// ==========================================
const OneTimeBookingWizard = () => {
  const [searchParams] = useSearchParams();
  // Phase 1: default to interior package; vehicle type comes from InteriorBooking page
  const packageParam = searchParams.get('package') || 'interior';
  const vehicleTypeParam = searchParams.get('vehicle') || 'sedan'; // sedan | suv | truck
  const isMaintenanceSignup = searchParams.get('maintenance') === 'true';
  const pkg = ONE_TIME_PACKAGES.find((p) => p.id === packageParam) || ONE_TIME_PACKAGES.find((p) => p.id === 'interior');

  // Two independent promo slots: ONE package promo (MONARCH15 / FB10 / FB15)
  // and the SEAT50 car-seat discount. They stack. Links use ?promo=MONARCH15
  // &seatpromo=SEAT50; SEAT50 typed or linked in either box lands in the seat slot.
  const splitCodes = (...vals) => vals
    .flatMap((v) => String(v || '').toUpperCase().split(/[\s,+]+/))
    .filter(Boolean);
  const initialCodes = splitCodes(searchParams.get('promo'), searchParams.get('seatpromo'));
  const [promoCodeInput, setPromoCodeInput] = useState(initialCodes.find((c) => c !== SEAT_PROMO_CODE) || '');
  const [seatCodeInput, setSeatCodeInput] = useState(initialCodes.includes(SEAT_PROMO_CODE) ? SEAT_PROMO_CODE : '');
  const PROMO_CODES = { FB15: 0.15, [LAUNCH_PROMO_CODE]: LAUNCH_PROMO_RATE, FB10: 0.10 };
  // If a code is typed in the wrong box, move it to the right one so the
  // customer always sees confirmation next to the box that applies it.
  const handlePackageCodeChange = (value) => {
    if (value.trim().toUpperCase() === SEAT_PROMO_CODE) {
      setSeatCodeInput(SEAT_PROMO_CODE);
      setPromoCodeInput('');
    } else {
      setPromoCodeInput(value);
    }
  };
  const handleSeatCodeChange = (value) => {
    const v = value.trim().toUpperCase();
    if (v && v !== SEAT_PROMO_CODE && PROMO_CODES[v]) {
      setPromoCodeInput(v);
      setSeatCodeInput('');
    } else {
      setSeatCodeInput(value);
    }
  };
  const typedCodes = splitCodes(promoCodeInput, seatCodeInput);
  const promoCode = typedCodes.find((c) => c !== SEAT_PROMO_CODE) || '';
  const seatTyped = typedCodes.includes(SEAT_PROMO_CODE);
  // MONARCH15, FB10 and SEAT50 all end at PROMO_EXPIRY_ISO — same as the API.
  // FB15 (raffle) has no end date.
  const PROMO_EXPIRY_MS = new Date(PROMO_EXPIRY_ISO).getTime();
  const promoWindowOpen = Date.now() <= PROMO_EXPIRY_MS;
  const promoDiscount = ([LAUNCH_PROMO_CODE, 'FB10'].includes(promoCode) && !promoWindowOpen)
    ? 0
    : (PROMO_CODES[promoCode] || 0);
  // The launch offer is capped at the first 10 bookings and closes
  // automatically. When it is the applied code (typed or via ?promo=), check
  // /api/promo-status: if the spots are gone, swap the customer to FB10
  // (10% off) with a clear notice instead of a dead end. If the check
  // fails for any reason, the code simply applies as it always has — a status
  // outage never blocks a booking. SEAT50 is unaffected by the swap.
  const [launchSwapped, setLaunchSwapped] = useState(false);
  useEffect(() => {
    if (promoCode !== LAUNCH_PROMO_CODE) return undefined;
    let cancelled = false;
    fetch('/api/promo-status')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.launch?.soldOut) return;
        setLaunchSwapped(true);
        setPromoCodeInput(FALLBACK_PROMO_CODE);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [promoCode]);
  // SEAT50 (Car Seat Special): 50% off the Car Seat add-on only, never the
  // package, and it combines with the package promo above.
  const seat50Active = seatTyped && promoWindowOpen;

  // Back to the package picker without losing either code.
  const detailQuery = `/detail${(promoCode || seat50Active) ? `?${new URLSearchParams({ ...(promoCode ? { promo: promoCode } : {}), ...(seat50Active ? { seatpromo: SEAT_PROMO_CODE } : {}) }).toString()}` : ''}`;

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [countyAutoDetected, setCountyAutoDetected] = useState(false);
  const [uploadSessionId] = useState(() => uuidv4());
  const [customerPhotos, setCustomerPhotos] = useState([]);
  const [photosUploading, setPhotosUploading] = useState(false);
  const [vehicleType, setVehicleType] = useState(vehicleTypeParam || 'sedan');

  // Pre-fill add-ons from URL (quiz or interior booking page)
  const addonsParam = searchParams.get('addons') || '';
  const preSelectedAddOns = addonsParam ? addonsParam.split(',').filter(Boolean) : [];

  // Pre-fill contact info from interior health check quiz
  const quizPrefill = (() => {
    try {
      const raw = sessionStorage.getItem('quiz_prefill');
      if (raw) { sessionStorage.removeItem('quiz_prefill'); return JSON.parse(raw); }
    } catch {}
    return null;
  })();

  const [formData, setFormData] = useState({
    personalInfo: {
      name: quizPrefill?.name || '',
      email: quizPrefill?.email || '',
      phone: quizPrefill?.phone || '',
      homeAddress: '',
      county: '',
    },
    vehicleInfo: { year: '', make: '', makeOther: '', model: '', modelOther: '', color: '', colorOther: '' },
    selectedSlot: null,
    selectedAddOns: preSelectedAddOns,
    acceptedAgreement: false,
    smsConsent: false,
    signature: '',
    customerMessage: '',
    referralCode: '',
  });

  // SEAT50 auto-adds the Car Seat add-on once when the code becomes active.
  // If the customer removes the add-on afterwards it stays removed (the
  // $17.50 seat discount just drops off in Step 3) — it is only re-added if
  // the code is cleared and applied again. Clearing the code never removes
  // the add-on; it just goes back to full price.
  const seat50AutoAddedFor = useRef('');
  useEffect(() => {
    if (seat50Active && seat50AutoAddedFor.current !== SEAT_PROMO_CODE) {
      seat50AutoAddedFor.current = SEAT_PROMO_CODE;
      setFormData((prev) => (prev.selectedAddOns.includes('car_seat')
        ? prev
        : { ...prev, selectedAddOns: [...prev.selectedAddOns, 'car_seat'] }));
    }
    if (!seat50Active) seat50AutoAddedFor.current = '';
  }, [seat50Active]);

  const handleAddPhoto = useCallback((file) => {
    const preview = URL.createObjectURL(file);
    setCustomerPhotos(prev => [...prev, { file, preview, uploading: false, url: null }]);
  }, []);

  const handleRemovePhoto = useCallback((index) => {
    setCustomerPhotos(prev => {
      const updated = [...prev];
      if (updated[index]?.preview) URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  const uploadPendingPhotos = async () => {
    const pending = customerPhotos.filter(p => !p.url);
    if (!pending.length) return;
    setPhotosUploading(true);
    const updated = [...customerPhotos];
    for (let i = 0; i < updated.length; i++) {
      if (updated[i].url) continue;
      try {
        updated[i] = { ...updated[i], uploading: true };
        setCustomerPhotos([...updated]);
        const { url } = await uploadCustomerPhoto(uploadSessionId, updated[i].file);
        updated[i] = { ...updated[i], url, uploading: false };
      } catch (err) {
        console.warn('Photo upload failed:', err.message);
        updated[i] = { ...updated[i], uploading: false };
      }
      setCustomerPhotos([...updated]);
    }
    setPhotosUploading(false);
  };

  const geocodeCounty = useCallback(async (address) => {
    if (!address || address.trim().length < 10) return;
    try {
      const res = await fetch('/api/geocode-county', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address }),
      });
      if (!res.ok) return;
      const { county } = await res.json();
      if (!county) return;
      setFormData(prev => ({
        ...prev,
        personalInfo: { ...prev.personalInfo, county },
      }));
      setCountyAutoDetected(true);
    } catch {}
  }, []);

  const handlePersonalChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const numeric = value.replace(/\D/g, '');
      if (numeric.length > 10) return;
      setFormData((prev) => ({ ...prev, personalInfo: { ...prev.personalInfo, phone: numeric } }));
      return;
    }
    setFormData((prev) => ({ ...prev, personalInfo: { ...prev.personalInfo, [name]: value } }));
  }, []);

  const handleVehicleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === 'make') {
      setFormData((prev) => ({ ...prev, vehicleInfo: { ...prev.vehicleInfo, make: value, model: '' } }));
      return;
    }
    setFormData((prev) => ({ ...prev, vehicleInfo: { ...prev.vehicleInfo, [name]: value } }));
  }, []);

  const handleSlotSelect = useCallback((slotId) => {
    setFormData((prev) => ({ ...prev, selectedSlot: slotId }));
  }, []);

  const handleAgreementChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  const handleAddOnToggle = useCallback((addonId) => {
    setFormData((prev) => {
      const current = prev.selectedAddOns;
      const already = current.includes(addonId);
      return { ...prev, selectedAddOns: already ? current.filter((id) => id !== addonId) : [...current, addonId] };
    });
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [currentStep]);

  // Meta InitiateCheckout: fired once, when the customer reaches the final
  // Review & Pay step with their details and date chosen — the campaign
  // optimization event. (Purchase is fired separately on /detail/success.)
  const initiateCheckoutSent = useRef(false);
  useEffect(() => {
    if (currentStep !== 3 || initiateCheckoutSent.current) return;
    initiateCheckoutSent.current = true;
    trackEvent('InitiateCheckout', {
      content_ids: [pkg.id],
      content_name: pkg.name,
      content_category: 'interior_detail',
      content_type: 'product',
      num_items: 1,
      currency: 'USD',
    });
  }, [currentStep, pkg]);

  const goNext = () => {
    const errors = validateStep(currentStep, formData);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowFieldErrors(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setValidationErrors([]);
    setShowFieldErrors(false);
    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  const goBack = () => {
    setValidationErrors([]);
    setShowFieldErrors(false);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handlePayment = async () => {
    if (isSubmitting) return;
    const errors = validateStep(3, formData);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowFieldErrors(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setIsSubmitting(true);
    setValidationErrors([]);
    await uploadPendingPhotos();
    try {
      const vi = formData.vehicleInfo;
      const resolvedVehicleInfo = {
        year: vi.year,
        make: vi.make === 'other' ? vi.makeOther : vi.make,
        model: vi.make === 'other' ? vi.modelOther : (vi.model === 'other' ? vi.modelOther : vi.model),
        color: vi.color,
      };
      const cleanData = sanitizeFormData({
        personalInfo: formData.personalInfo,
        vehicleInfo: resolvedVehicleInfo,
        selectedSlot: formData.selectedSlot,
        signature: formData.signature,
        packageId: pkg.id,
        packageName: pkg.name,
        customerMessage: formData.customerMessage || '',
      });
      const response = await fetch('/api/create-onetime-stripe-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userData: cleanData,
          selectedSlot: formData.selectedSlot,
          packageId: pkg.id,
          vehicleType: vehicleType,
          addOns: pkg.id === 'alchemist' ? formData.selectedAddOns.filter((id) => id !== 'steam_clean' && id !== 'carpet_shampoo') : formData.selectedAddOns,
          travelFee: formData.personalInfo.county === 'outside',
          uploadSessionId: customerPhotos.length ? uploadSessionId : '',
          isMaintenanceSignup,
          promoCode: promoCode || undefined,
          seatPromoCode: seat50Active ? SEAT_PROMO_CODE : undefined,
          referralCode: formData.referralCode.trim() || undefined,
          tracking: getTrackingPayload(),
        }),
      });
      if (!response.ok) {
        const err = await response.json();
        if (err.code === 'PROMO_EXPIRED') {
          // The offers ended while they were booking: drop the codes, show the
          // regular total, let them pay again.
          setPromoCodeInput('');
          setSeatCodeInput('');
          setValidationErrors([err.error || 'That offer has ended. We removed the code — check your updated total and tap Pay again.']);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        if (err.code === 'LAUNCH_PROMO_SOLD_OUT') {
          // The first-10 spots closed while they were booking: switch them to
          // the FB10 fallback and let them pay again with one click.
          setLaunchSwapped(true);
          setPromoCodeInput(FALLBACK_PROMO_CODE);
          setValidationErrors([`Sorry — ${LAUNCH_PROMO_CODE} just sold out (the first ${LAUNCH_PROMO_CAP} spots are gone). We've applied ${FALLBACK_PROMO_CODE} for 10% off instead — check your updated total below and tap Pay again.`]);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        throw new Error(err.error || 'Failed to create payment session');
      }
      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error('One-time payment error:', err);
      setValidationErrors([err.message || 'Unable to start payment. Please try again or contact support.']);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">

      {/* Progress Bar */}
      <section className="bg-white py-6 px-6 border-b-4 border-[#68caf1] sticky top-[73px] z-40">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col items-center justify-center mb-5">
            <span className="bg-black text-[#68caf1] text-xs font-bold px-5 py-1.5 rounded-full tracking-widest uppercase">
              {pkg.emoji} {pkg.name} — ${vehicleType === 'truck' ? (pkg.truckPrice || pkg.price) : vehicleType === 'suv' ? (pkg.suvPrice || pkg.price) : pkg.price}
            </span>
            <Link to={detailQuery} className="mt-2 text-xs font-semibold text-[#0e87b3] hover:underline underline-offset-2">
              Change package
            </Link>
          </div>
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className={`flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full font-bold text-sm md:text-lg transition-all duration-300 ${
                  currentStep >= step.id ? 'bg-[#68caf1] text-black shadow-lg' : 'bg-gray-200 text-gray-500'
                }`}>
                  {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
                </div>
                {idx < STEPS.length - 1 && (
                  <div className="flex-1 mx-1 md:mx-2">
                    <div className={`h-2 rounded-full transition-all duration-500 ${currentStep > step.id ? 'bg-[#68caf1]' : 'bg-gray-200'}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-1 md:gap-2 mt-4 text-xs md:text-sm font-semibold">
            {STEPS.map((step) => (
              <span key={step.id} className={`text-center transition-colors ${currentStep >= step.id ? 'text-[#68caf1]' : 'text-gray-500'}`}>
                {step.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="max-w-4xl mx-auto px-6 pt-6">
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-red-800 mb-1">Please fix the following:</p>
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                  {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="py-12 px-6">
        <div className="max-w-4xl mx-auto">

          {/* ======== STEP 1: Personal & Vehicle Info ======== */}
          {currentStep === 1 && (
            <>
              <h2 className="text-4xl md:text-5xl font-bold text-[#333333] mb-4 text-center">Your Information</h2>
              <p className="text-center text-gray-600 mb-8">Tell us about yourself and your vehicle</p>

              {/* Personal Info */}
              <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border-l-8 border-[#68caf1]">
                <div className="flex items-center gap-3 mb-6">
                  <User className="w-8 h-8 text-[#68caf1]" />
                  <h3 className="text-2xl font-bold text-[#333333]">Personal Information</h3>
                </div>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text" name="name" value={formData.personalInfo.name} onChange={handlePersonalChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-gray-900 bg-white transition-all ${showFieldErrors && !formData.personalInfo.name.trim() ? 'border-red-500 ring-2 ring-red-200 bg-red-50' : 'border-gray-300 focus:border-[#68caf1]'}`}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                      <input
                        type="tel" name="phone" value={formData.personalInfo.phone} onChange={handlePersonalChange}
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-gray-900 bg-white transition-all ${showFieldErrors && (!formData.personalInfo.phone.trim() || formData.personalInfo.phone.length !== 10) ? 'border-red-500 ring-2 ring-red-200 bg-red-50' : 'border-gray-300 focus:border-[#68caf1]'}`}
                        placeholder="1234567890" maxLength="10"
                      />
                      <p className="text-xs text-gray-500 mt-1">10 digits, numbers only</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                      <input
                        type="email" name="email" value={formData.personalInfo.email} onChange={handlePersonalChange}
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-gray-900 bg-white transition-all ${showFieldErrors && !formData.personalInfo.email.trim() ? 'border-red-500 ring-2 ring-red-200 bg-red-50' : 'border-gray-300 focus:border-[#68caf1]'}`}
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <label className={`flex items-start gap-3 cursor-pointer p-3 rounded-lg border-2 transition-colors ${showFieldErrors && !formData.smsConsent ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-[#68caf1]/50'}`}>
                    <input
                      type="checkbox"
                      checked={formData.smsConsent}
                      onChange={e => setFormData(prev => ({ ...prev, smsConsent: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 accent-[#68caf1] shrink-0"
                    />
                    <span className="text-xs text-gray-600 leading-relaxed">
                      I agree to receive booking confirmations, service updates, and invoices via SMS from Monarch Detailing. Msg & data rates may apply. Reply <strong>STOP</strong> to unsubscribe at any time. *
                    </span>
                  </label>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Service Address *</label>
                    <div className={showFieldErrors && !formData.personalInfo.homeAddress.trim() ? 'rounded-lg ring-2 ring-red-500' : ''}>
                      <AddressAutocomplete
                        name="homeAddress" value={formData.personalInfo.homeAddress} onChange={handlePersonalChange}
                        onBlur={(e) => geocodeCounty(e.target.value)}
                        placeholder="123 Main St, City, MD ZIP"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#68caf1] focus:outline-none text-gray-900"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">County *</label>
                    <select
                      name="county" value={formData.personalInfo.county}
                      onChange={(e) => { handlePersonalChange(e); setCountyAutoDetected(false); }}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-gray-900 bg-white transition-all ${showFieldErrors && !formData.personalInfo.county ? 'border-red-500 ring-2 ring-red-200 bg-red-50' : 'border-gray-300 focus:border-[#68caf1]'}`}
                    >
                      <option value="">Select your county…</option>
                      <option value="St. Mary's County">St. Mary's County</option>
                      <option value="Charles County">Charles County</option>
                      <option value="Calvert County">Calvert County</option>
                      <option value="outside">Outside Service Area</option>
                    </select>
                    {countyAutoDetected && formData.personalInfo.county && formData.personalInfo.county !== 'outside' && (
                      <p className="mt-2 text-sm text-green-600 font-medium">&#10003; Verified from your address</p>
                    )}
                    {countyAutoDetected && formData.personalInfo.county === 'outside' && (
                      <p className="mt-2 text-sm text-amber-600 font-medium">&#128205; Address is outside our primary service area &mdash; $35 travel fee applies</p>
                    )}
                    {formData.personalInfo.county === 'outside' && (
                      <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <p className="text-amber-700 font-semibold text-sm">Outside Primary Service Area</p>
                        <p className="text-amber-600 text-xs mt-1">You can still book — a $35 travel fee will be added to your total.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 border-l-8 border-[#68caf1]">
                <div className="flex items-center gap-3 mb-4">
                  <Car className="w-8 h-8 text-[#68caf1]" />
                  <h3 className="text-2xl font-bold text-[#333333]">Vehicle Information</h3>
                </div>

                {/* Vehicle type tier selector */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Vehicle Type *</p>
                  <div className="grid grid-cols-3 gap-3">
                    {INTERIOR_VEHICLE_TYPES.map(vt => (
                      <button
                        key={vt.id}
                        type="button"
                        onClick={() => setVehicleType(vt.id)}
                        className={`flex flex-col items-center py-3 px-2 rounded-xl border-2 text-center transition-all ${
                          vehicleType === vt.id
                            ? 'bg-[#68caf1] border-[#68caf1] text-black'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-[#68caf1]'
                        }`}
                      >
                        <span className="text-2xl mb-1">{vt.icon}</span>
                        <span className="text-xs font-bold leading-tight">{vt.label}</span>
                        <span className="text-xs mt-1 font-bold">${vt.id === 'truck' ? (pkg.truckPrice || pkg.price) : vt.id === 'suv' ? (pkg.suvPrice || pkg.price) : pkg.price}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Make *</label>
                    <select name="make" value={formData.vehicleInfo.make} onChange={handleVehicleChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-gray-900 bg-white transition-all ${showFieldErrors && !formData.vehicleInfo.make ? 'border-red-500 ring-2 ring-red-200 bg-red-50' : 'border-gray-300 focus:border-[#68caf1]'}`}>
                      <option value="">Select Make</option>
                      {carMakes.map((make) => <option key={make} value={make}>{make}</option>)}
                      <option value="other">Other (type yours)</option>
                    </select>
                    {formData.vehicleInfo.make === 'other' && (
                      <input type="text" name="makeOther" value={formData.vehicleInfo.makeOther} onChange={handleVehicleChange}
                        className="w-full mt-2 px-4 py-3 border-2 border-[#68caf1] rounded-lg focus:outline-none text-gray-900"
                        placeholder="Type your vehicle make" autoFocus />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Model *</label>
                    {formData.vehicleInfo.make === 'other' ? (
                      <input type="text" name="modelOther" value={formData.vehicleInfo.modelOther} onChange={handleVehicleChange}
                        className="w-full px-4 py-3 border-2 border-[#68caf1] rounded-lg focus:outline-none text-gray-900"
                        placeholder="Type your vehicle model" />
                    ) : (
                      <>
                        <select name="model" value={formData.vehicleInfo.model}
                          onChange={(e) => {
                            handleVehicleChange(e);
                            const detected = detectVehicleType(formData.vehicleInfo.make, e.target.value);
                            if (detected) setVehicleType(detected);
                          }}
                          className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-gray-900 bg-white transition-all ${showFieldErrors && !formData.vehicleInfo.model ? 'border-red-500 ring-2 ring-red-200 bg-red-50' : 'border-gray-300 focus:border-[#68caf1]'}`}
                          disabled={!formData.vehicleInfo.make}>
                          <option value="">Select Model</option>
                          {formData.vehicleInfo.make && carModels[formData.vehicleInfo.make]?.map((model) => (
                            <option key={model} value={model}>{model}</option>
                          ))}
                          {formData.vehicleInfo.make && <option value="other">Other (type yours)</option>}
                        </select>
                        {formData.vehicleInfo.model === 'other' && (
                          <input type="text" name="modelOther" value={formData.vehicleInfo.modelOther} onChange={handleVehicleChange}
                            className="w-full mt-2 px-4 py-3 border-2 border-[#68caf1] rounded-lg focus:outline-none text-gray-900"
                            placeholder="Type your vehicle model" autoFocus />
                        )}
                      </>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Year *</label>
                    <select name="year" value={formData.vehicleInfo.year} onChange={handleVehicleChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-gray-900 bg-white transition-all ${showFieldErrors && !formData.vehicleInfo.year ? 'border-red-500 ring-2 ring-red-200 bg-red-50' : 'border-gray-300 focus:border-[#68caf1]'}`}>
                      <option value="">Select Year</option>
                      {years.map((year) => <option key={year} value={year}>{year}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Color *</label>
                  <select
                    value={formData.vehicleInfo.colorOther === 'other' ? 'other' : formData.vehicleInfo.color}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        vehicleInfo: {
                          ...prev.vehicleInfo,
                          color: val === 'other' ? '' : val,
                          colorOther: val === 'other' ? 'other' : '',
                        },
                      }));
                    }}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-gray-900 md:w-1/3 bg-white transition-all ${showFieldErrors && !formData.vehicleInfo.color.trim() && formData.vehicleInfo.colorOther !== 'other' ? 'border-red-500 ring-2 ring-red-200 bg-red-50' : 'border-gray-300 focus:border-[#68caf1]'}`}
                  >
                    <option value="">Select a color</option>
                    {['White','Pearl White','Black','Silver','Gray','Charcoal','Red','Blue','Navy','Green','Brown','Beige','Champagne','Gold','Orange','Yellow','Purple','Maroon','Burgundy'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                    <option value="other">Other (type yours)</option>
                  </select>
                  {formData.vehicleInfo.colorOther === 'other' && (
                    <input
                      type="text"
                      name="color"
                      value={formData.vehicleInfo.color}
                      onChange={handleVehicleChange}
                      className="w-full mt-2 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#68caf1] focus:outline-none text-gray-900 md:w-1/3"
                      placeholder="Type your color (e.g. Midnight Blue)"
                      autoFocus
                    />
                  )}
                </div>
              </div>

              {/* Promo Code */}
              <div className="bg-white rounded-2xl p-6 shadow-lg mb-8 border-l-8 border-[#68caf1]">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">🎉</span>
                  <h3 className="text-lg font-bold text-[#333333]">Have a Promo Code?</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Saw a code on Facebook or a flyer? Enter it here. A package code ({LAUNCH_PROMO_CODE} or {FALLBACK_PROMO_CODE}) and the car seat code ({SEAT_PROMO_CODE}) can be used together.
                </p>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Package code</label>
                <input
                  type="text"
                  value={promoCodeInput}
                  onChange={(e) => handlePackageCodeChange(e.target.value)}
                  className="w-full md:w-64 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#68caf1] focus:outline-none text-gray-900 font-mono uppercase tracking-widest"
                  placeholder={`e.g. ${LAUNCH_PROMO_CODE}`}
                  maxLength={12}
                />
                {launchSwapped && promoCode === FALLBACK_PROMO_CODE ? (
                  <p className="text-sm mt-2 font-semibold text-emerald-600">
                    {LAUNCH_PROMO_CODE} is sold out — the first {LAUNCH_PROMO_CAP} spots are gone. Here's 10% off instead: {FALLBACK_PROMO_CODE} is applied.
                  </p>
                ) : promoCode && (
                  <p className={`text-sm mt-2 font-semibold ${promoDiscount > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {promoDiscount > 0
                      ? `✓ ${promoCode} applied — ${Math.round(promoDiscount * 100)}% off your package`
                      : 'That code isn\'t valid or has expired.'}
                  </p>
                )}

                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mt-5 mb-1">Car seat code</label>
                <input
                  type="text"
                  value={seatCodeInput}
                  onChange={(e) => handleSeatCodeChange(e.target.value)}
                  className="w-full md:w-64 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#68caf1] focus:outline-none text-gray-900 font-mono uppercase tracking-widest"
                  placeholder={`e.g. ${SEAT_PROMO_CODE}`}
                  maxLength={12}
                />
                {seatCodeInput.trim() && (
                  <p className={`text-sm mt-2 font-semibold ${seat50Active ? 'text-emerald-600' : 'text-red-500'}`}>
                    {seat50Active
                      ? `✓ ${SEAT_PROMO_CODE} applied — Car Seat Deep Clean ${Math.round(SEAT_PROMO_RATE * 100)}% off (added to your booking)`
                      : (seatTyped ? 'That code has expired.' : 'That code isn\'t valid.')}
                  </p>
                )}
              </div>

              {/* Referral Code */}
              <div className="bg-white rounded-2xl p-6 shadow-lg mb-8 border-l-8 border-amber-400">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">⭐</span>
                  <h3 className="text-lg font-bold text-[#333333]">Have a Referral Code?</h3>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  A friend who's been detailed by Akeem should have a code to share. Enter it here and you'll both get $15 off.
                </p>
                <input
                  type="text"
                  value={formData.referralCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, referralCode: e.target.value.toUpperCase() }))}
                  className="w-full md:w-64 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-amber-400 focus:outline-none text-gray-900 font-mono uppercase tracking-widest"
                  placeholder="e.g. JANE-4821"
                  maxLength={12}
                />
              </div>

              <div className="flex justify-between items-center">
                <Link to={detailQuery} className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-100 transition-all inline-flex items-center gap-2">
                  <ChevronLeft className="w-5 h-5" /> Back
                </Link>
                <button onClick={goNext} className="bg-[#68caf1] text-black px-12 py-4 rounded-lg font-bold text-lg hover:bg-[#5bb8db] transition-all shadow-lg inline-flex items-center gap-2">
                  Continue <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </>
          )}

          {/* ======== STEP 2: Pick a Date + Add-Ons ======== */}
          {currentStep === 2 && (() => {
            const ALCHEMIST_INCLUDED_ADD_ONS = ['steam_clean', 'carpet_shampoo'];
            const isAlchemist = pkg.id === 'alchemist';
            const chargeableAddOns = isAlchemist
              ? formData.selectedAddOns.filter((id) => !ALCHEMIST_INCLUDED_ADD_ONS.includes(id))
              : formData.selectedAddOns;
            const addOnsTotal = chargeableAddOns.reduce((sum, id) => {
              const a = ADD_ONS.find((x) => x.id === id);
              return sum + (a ? a.price : 0);
            }, 0);
            return (
              <>
                <h2 className="text-4xl md:text-5xl font-bold text-[#333333] mb-4 text-center">Pick a Date</h2>
                <p className="text-center text-gray-600 mb-12">Choose your slot — then add any extras below</p>

                <OneTimeCalendar selectedSlot={formData.selectedSlot} onSelectSlot={handleSlotSelect} />

                {/* Rush request — handled by text, Akeem decides case by case */}
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-amber-900">Day taken, or need it sooner?</p>
                    <p className="text-xs text-amber-800 mt-0.5">Same-day and extra-car requests are +${RUSH_FEE} and confirmed by Akeem over text — based on his schedule.</p>
                  </div>
                  <a
                    href={`sms:${SHOP_PHONE_SMS}?&body=${encodeURIComponent(`Hi Akeem, I'd like to request a rush / same-day ${pkg.name} detail (+$${RUSH_FEE}). Vehicle: ${[formData.vehicleInfo.year, formData.vehicleInfo.make, formData.vehicleInfo.model].filter(Boolean).join(' ') || '(sedan/SUV/truck)'}. Day I need: `)}`}
                    onClick={() => trackEvent('Contact', { content_name: 'rush_request', content_category: pkg.id })}
                    className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-4 py-2.5 rounded-lg transition-all whitespace-nowrap"
                  >
                    Text for a Rush
                  </a>
                </div>

                {/* Compact Add-Ons */}
                <div className="bg-white rounded-2xl p-6 shadow-lg mt-8 border-l-8 border-[#68caf1]">
                  <div className="flex items-center gap-3 mb-4">
                    <Sparkles className="w-6 h-6 text-[#68caf1]" />
                    <h3 className="text-xl font-bold text-[#333333]">Optional Add-Ons</h3>
                    {addOnsTotal > 0 && (
                      <span className="ml-auto text-[#68caf1] font-bold text-sm">+${addOnsTotal} added</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {ADD_ONS.filter((addon) => !(isAlchemist && ALCHEMIST_INCLUDED_ADD_ONS.includes(addon.id))).map((addon) => {
                      const selected = formData.selectedAddOns.includes(addon.id);
                      return (
                        <button key={addon.id} type="button" onClick={() => handleAddOnToggle(addon.id)}
                          className={`relative text-left rounded-xl p-3 transition-all duration-200 border-2 ${
                            selected
                              ? 'border-[#68caf1] bg-[#68caf1]/10 shadow-md'
                              : 'border-gray-200 bg-gray-50 hover:border-[#68caf1]/50'
                          }`}
                        >
                          {selected && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-[#68caf1] rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-black" />
                            </div>
                          )}
                          <div className="text-2xl mb-1 leading-none">{addon.emoji}</div>
                          <div className="text-xs font-bold text-[#333333] leading-tight mb-0.5">{addon.label}</div>
                          <div className={`text-xs font-semibold ${selected ? 'text-[#68caf1]' : 'text-gray-500'}`}>+${addon.price}</div>
                        </button>
                      );
                    })}
                  </div>
                  {isAlchemist && (
                    <p className="text-sm text-emerald-600 font-semibold mt-3">
                      ✓ Included in The Alchemist: Steam Treatment and Carpet &amp; Seat Deep Shampoo — no extra charge.
                    </p>
                  )}
                  <p className="text-xs text-gray-400 italic mt-3">
                    *Prices subject to vehicle inspection. Additional charges may apply for heavy soiling, excessive pet hair, or biohazard conditions. All charges are quoted on-site before work begins — you must approve before any additional work starts.
                  </p>
                </div>

                {/* Client Message */}
                <div className="bg-white rounded-2xl p-6 shadow-lg mt-6 border-l-8 border-[#68caf1]">
                  <div className="flex items-center gap-3 mb-3">
                    <MessageSquare className="w-6 h-6 text-[#68caf1]" />
                    <h3 className="text-xl font-bold text-[#333333]">Message Akeem Directly</h3>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Use this for anything — problem areas, stains, a different appointment time, questions, special requests. This goes straight to Akeem. You can also reach him directly at <span className="font-semibold text-[#333333]">(301) 885-9767</span>.
                  </p>
                  <textarea
                    value={formData.customerMessage}
                    onChange={(e) => setFormData((prev) => ({ ...prev, customerMessage: e.target.value }))}
                    placeholder="e.g. Can we do a different day? Also there's a coffee stain on the passenger seat and some pet hair in the back..."
                    rows={5} maxLength={500}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#68caf1] focus:outline-none text-gray-900 resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{formData.customerMessage.length}/500</p>
                  <CustomerPhotoUpload
                    photos={customerPhotos}
                    onAdd={handleAddPhoto}
                    onRemove={handleRemovePhoto}
                    uploading={photosUploading}
                  />
                </div>

                <div className="flex justify-between items-center mt-8">
                  <button onClick={goBack} className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-100 transition-all inline-flex items-center gap-2">
                    <ChevronLeft className="w-5 h-5" /> Back
                  </button>
                  <button onClick={goNext} className="bg-[#68caf1] text-black px-12 py-4 rounded-lg font-bold text-lg hover:bg-[#5bb8db] transition-all shadow-lg inline-flex items-center gap-2">
                    Continue <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </>
            );
          })()}

          {/* ======== STEP 3: Review & Pay ======== */}
          {currentStep === 3 && (() => {
            const isOutsideArea = formData.personalInfo.county === 'outside';
            const pkgPrice = vehicleType === 'truck' ? (pkg.truckPrice || pkg.price) : vehicleType === 'suv' ? (pkg.suvPrice || pkg.price) : pkg.price;
            const pkgPriceCents = vehicleType === 'truck' ? (pkg.truckPriceCents || pkg.priceCents) : vehicleType === 'suv' ? (pkg.suvPriceCents || pkg.priceCents) : pkg.priceCents;
            const ALCHEMIST_INCLUDED_ADD_ONS = ['steam_clean', 'carpet_shampoo'];
            const chargeableAddOns = pkg.id === 'alchemist'
              ? formData.selectedAddOns.filter((id) => !ALCHEMIST_INCLUDED_ADD_ONS.includes(id))
              : formData.selectedAddOns;
            // Totals in integer cents — identical arithmetic to computeOneTimeTotals()
            // in api/_utils.js, so this screen always equals the Stripe charge:
            //  • the package promo discounts the PACKAGE price only
            //  • SEAT50 discounts the car-seat add-on only (when it's in the cart)
            //  • both apply together; deposit = 50% of the final total
            const carSeatAddOn = ADD_ONS.find((x) => x.id === 'car_seat');
            const addOnsCents = chargeableAddOns.reduce((sum, id) => {
              const a = ADD_ONS.find((x) => x.id === id);
              return sum + (a ? a.priceCents : 0);
            }, 0);
            const travelFeeCents = isOutsideArea ? 3500 : 0;
            const subtotalCents = pkgPriceCents + addOnsCents + travelFeeCents;
            const packageDiscountCents = promoDiscount > 0
              ? Math.round((pkgPriceCents * Math.round(promoDiscount * 100)) / 100)
              : 0;
            const seat50DiscountCents = seat50Active && carSeatAddOn && chargeableAddOns.includes('car_seat')
              ? Math.round((carSeatAddOn.priceCents * Math.round(SEAT_PROMO_RATE * 100)) / 100)
              : 0;
            const grandTotalCents = Math.max(subtotalCents - packageDiscountCents - seat50DiscountCents, 5000);
            const depositCents = Math.round(grandTotalCents / 2);
            const depositDue = depositCents / 100;
            const balanceDue = (grandTotalCents - depositCents) / 100;
            const grandTotalBeforePromo = subtotalCents / 100;
            const grandTotal = grandTotalCents / 100;
            const seat50Discount = seat50DiscountCents / 100;
            return (
              <>
                <h2 className="text-4xl md:text-5xl font-bold text-[#333333] mb-4 text-center">Review & Pay</h2>
                <p className="text-center text-gray-600 mb-12">Lock in your detail — secure checkout via Stripe</p>

                {/* Price Breakdown */}
                <div className="bg-white rounded-2xl p-8 shadow-lg mb-6 border-l-8 border-[#68caf1]">
                  <div className="flex items-center gap-3 mb-6">
                    <DollarSign className="w-8 h-8 text-[#68caf1]" />
                    <h3 className="text-2xl font-bold text-[#333333]">Booking Summary</h3>
                  </div>

                  <div className="bg-gradient-to-br from-[#68caf1]/10 to-white rounded-xl p-6 mb-6 border-2 border-[#68caf1] space-y-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-gray-700">{pkg.emoji} {pkg.name}</p>
                        <p className="text-xs text-gray-500">Base service</p>
                      </div>
                      <span className="font-bold text-gray-800">${pkgPrice}</span>
                    </div>
                    {chargeableAddOns.map((id) => {
                      const a = ADD_ONS.find((x) => x.id === id);
                      if (!a) return null;
                      return (
                        <div key={id} className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">{a.emoji} {a.label}</span>
                          <span className="font-semibold text-gray-800">+${a.price}</span>
                        </div>
                      );
                    })}
                    {isOutsideArea && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">🚗 Travel Fee (Outside Service Area)</span>
                        <span className="font-semibold text-gray-800">+$35</span>
                      </div>
                    )}
                    <div className="border-t border-[#68caf1]/30 pt-3 space-y-2">
                      {(packageDiscountCents > 0 || seat50DiscountCents > 0) && (
                        <>
                          <div className="flex justify-between items-center text-sm text-gray-500">
                            <span>Subtotal</span>
                            <span>${grandTotalBeforePromo.toFixed(2)}</span>
                          </div>
                          {packageDiscountCents > 0 && (
                            <div className="flex justify-between items-center text-sm text-emerald-600 font-semibold">
                              <span>🎉 {promoCode} — {Math.round(promoDiscount * 100)}% off your package</span>
                              <span>−${(packageDiscountCents / 100).toFixed(2)}</span>
                            </div>
                          )}
                          {seat50Discount > 0 && (
                            <div className="flex justify-between items-center text-sm text-emerald-600 font-semibold">
                              <span>🎉 {SEAT_PROMO_CODE} — 50% off your car seat add-on</span>
                              <span>−${seat50Discount.toFixed(2)}</span>
                            </div>
                          )}
                        </>
                      )}
                      <div className="flex justify-between items-center text-sm text-gray-500">
                        <span>Service Total</span>
                        <span>${grandTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="font-bold text-[#333333]">Deposit Due Today <span className="text-xs font-normal text-gray-500">(50%)</span></p>
                        <span className="text-3xl font-bold text-[#68caf1]">${depositDue.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Balance Due at Service</span>
                        <span className="font-semibold text-amber-600">${balanceDue.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 italic mt-1 mb-4">
                    *Prices subject to vehicle inspection. Additional charges may apply for heavy soiling, excessive pet hair, or biohazard conditions — always quoted and approved on-site before work begins.
                  </p>

                  {/* Booking Details */}
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h4 className="font-bold text-lg mb-4 text-[#333333]">Booking Details</h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Name:</span>
                        <span className="font-semibold text-gray-900">{formData.personalInfo.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-semibold text-gray-900">{formData.personalInfo.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-semibold text-gray-900">{formData.personalInfo.phone}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Service Address:</span>
                        <span className="font-semibold text-gray-900 text-right max-w-[55%]">{formData.personalInfo.homeAddress}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Vehicle:</span>
                        <span className="font-semibold text-gray-900">
                          {formData.vehicleInfo.year} {formData.vehicleInfo.make} {formData.vehicleInfo.model}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date & Time:</span>
                        <span className="font-semibold text-[#68caf1]">
                          {formData.selectedSlot ? formatOnetimeSlotLabel(formData.selectedSlot) : '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Package Expectations */}
                <div className="bg-white rounded-2xl p-6 shadow-lg mb-6 border-l-8 border-[#68caf1]">
                  <h3 className="text-xl font-bold text-[#333333] mb-4">{pkg.emoji} {pkg.name} — What to Expect</h3>
                  <p className="text-sm font-semibold text-gray-700 mb-2">This package includes:</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-4">
                    {pkg.features.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  {pkg.notIncluded && pkg.notIncluded.length > 0 && (
                    <>
                      <p className="text-sm font-semibold text-gray-700 mb-2">This package does NOT include:</p>
                      <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                        {pkg.notIncluded.map((f) => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>

                {/* Compact Service Agreement */}
                <div className="bg-white rounded-2xl p-6 shadow-lg mb-6 border-l-8 border-[#68caf1]">
                  <div className="flex items-center gap-3 mb-4">
                    <FileText className="w-6 h-6 text-[#68caf1]" />
                    <h3 className="text-xl font-bold text-[#333333]">Service Agreement</h3>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-200 text-sm text-gray-700 space-y-1.5">
                    <p><strong>Payment:</strong> 50% deposit today · balance due at service.</p>
                    <p><strong>Personal Items:</strong> Remove all belongings before service. We are not liable for items left in the vehicle.</p>
                    <p><strong>Cancellations:</strong> 48h notice required · deposit non-refundable for same-day cancellations.</p>
                  </div>
                  <button onClick={() => setShowAgreement(true)}
                    className="inline-flex items-center gap-2 text-[#68caf1] font-semibold text-sm hover:text-[#5bb8db] transition-colors mb-4">
                    <FileText className="w-4 h-4" /> Read Full Agreement
                  </button>
                  <label className={`flex items-start gap-3 cursor-pointer mb-4 rounded-lg p-3 border-2 transition-all ${
                    showFieldErrors && !formData.acceptedAgreement ? 'border-red-500 bg-red-50 ring-2 ring-red-500' : 'border-transparent'
                  }`}>
                    <input type="checkbox" name="acceptedAgreement" checked={formData.acceptedAgreement}
                      onChange={handleAgreementChange} className="w-5 h-5 mt-0.5 accent-[#68caf1]" />
                    <span className="text-sm text-gray-700">
                      I have read and agree to the One-Time Detail Service Agreement *
                    </span>
                  </label>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Digital Signature *</label>
                    <input type="text" name="signature" value={formData.signature} onChange={handleAgreementChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-2xl text-gray-900 transition-all ${
                        showFieldErrors && !formData.signature.trim() ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300 focus:border-[#68caf1]'
                      }`}
                      placeholder="Type your full name"
                      style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}
                    />
                    <p className="text-xs text-gray-500 mt-1">This constitutes your legal electronic signature</p>
                  </div>
                </div>

                {/* Personal Items Warning */}
                <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-5 mb-6 flex items-start gap-4">
                  <span className="text-3xl leading-none mt-1">🧹</span>
                  <div>
                    <p className="font-bold text-amber-900 text-base mb-1">Remove all personal items before your appointment</p>
                    <p className="text-amber-800 text-sm leading-relaxed">Please clear out all personal belongings — bags, clothing, documents, valuables — before Akeem arrives. This lets us get straight to work with zero delays. Monarch Detailing is not responsible for personal items left in the vehicle.</p>
                  </div>
                </div>

                {/* Pay Button */}
                <button
                  onClick={handlePayment}
                  disabled={isSubmitting}
                  className={`w-full px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-lg flex items-center justify-center gap-3 ${
                    isSubmitting ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-[#635bff] text-white hover:bg-[#5048e5]'
                  }`}
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-6 h-6 animate-spin" /> Connecting to Stripe...</>
                  ) : (
                    <><CreditCard className="w-6 h-6" /> Pay ${depositDue.toFixed(2)} Deposit & Reserve My Slot</>
                  )}
                </button>
                <p className="text-xs text-center text-gray-500 mt-3 mb-8">
                  You'll be redirected to Stripe's secure checkout. Your 50% deposit reserves your slot — the remaining ${balanceDue.toFixed(2)} is due at service.
                </p>

                <div className="flex justify-start">
                  <button onClick={goBack} className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-100 transition-all inline-flex items-center gap-2">
                    <ChevronLeft className="w-5 h-5" /> Back
                  </button>
                </div>
              </>
            );
          })()}

        </div>
      </div>

      <OneTimeAgreementModal isOpen={showAgreement} onClose={() => setShowAgreement(false)} />
    </div>
  );
};

export default OneTimeBookingWizard;
